/**
 * CarCortex Analytics Tracking Script
 * Tracks visitor behavior for marketing insights
 */

const API = process.env.REACT_APP_BACKEND_URL;

// Generate or get visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('carcortex_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('carcortex_visitor_id', visitorId);
  }
  return visitorId;
};

// Generate session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('carcortex_session_id');
  if (!sessionId) {
    sessionId = 's_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('carcortex_session_id', sessionId);
    sessionStorage.setItem('carcortex_session_start', Date.now().toString());
    sessionStorage.setItem('carcortex_page_count', '0');
  }
  return sessionId;
};

// Get UTM parameters from URL
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content')
  };
};

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Get browser name
const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('MSIE') || ua.includes('Trident')) return 'IE';
  return 'Other';
};

// Get OS name
const getOS = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Other';
};

// Get country from browser (approximate)
const getCountry = () => {
  // Use Intl API for approximate country detection
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const countryMap = {
      'Europe/Berlin': 'Germany',
      'Europe/Vienna': 'Austria',
      'Europe/Zurich': 'Switzerland',
      'Europe/London': 'United Kingdom',
      'Europe/Paris': 'France',
      'Europe/Rome': 'Italy',
      'Europe/Madrid': 'Spain',
      'Europe/Amsterdam': 'Netherlands',
      'Europe/Brussels': 'Belgium',
      'Europe/Warsaw': 'Poland',
      'Europe/Prague': 'Czech Republic',
      'Europe/Budapest': 'Hungary',
      'Europe/Stockholm': 'Sweden',
      'Europe/Oslo': 'Norway',
      'Europe/Copenhagen': 'Denmark',
      'Europe/Helsinki': 'Finland',
      'America/New_York': 'United States',
      'America/Los_Angeles': 'United States',
      'America/Chicago': 'United States',
      'America/Toronto': 'Canada',
      'Australia/Sydney': 'Australia',
      'Asia/Tokyo': 'Japan',
      'Asia/Shanghai': 'China'
    };
    return countryMap[timezone] || 'Unknown';
  } catch {
    return 'Unknown';
  }
};

// Track page view
export const trackPageView = async (pageTitle) => {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const utm = getUTMParams();
    
    // Increment page count
    const pageCount = parseInt(sessionStorage.getItem('carcortex_page_count') || '0') + 1;
    sessionStorage.setItem('carcortex_page_count', pageCount.toString());
    
    const payload = {
      page_url: window.location.pathname + window.location.search,
      page_title: pageTitle || document.title,
      referrer: document.referrer || null,
      visitor_id: visitorId,
      session_id: sessionId,
      country: getCountry(),
      device_type: getDeviceType(),
      browser: getBrowser(),
      os: getOS(),
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      ...utm
    };
    
    // Non-blocking fetch
    fetch(`${API}/api/analytics/track/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(() => {}); // Silently fail
    
  } catch (error) {
    console.debug('Analytics tracking error:', error);
  }
};

// Track session start
export const trackSessionStart = () => {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    
    fetch(`${API}/api/analytics/track/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'session_start',
        session_id: sessionId,
        visitor_id: visitorId
      }),
      keepalive: true
    }).catch(() => {});
    
  } catch (error) {
    console.debug('Session start tracking error:', error);
  }
};

// Track session end (called on page unload)
export const trackSessionEnd = (exitReason = 'navigation') => {
  try {
    const sessionId = sessionStorage.getItem('carcortex_session_id');
    const visitorId = localStorage.getItem('carcortex_visitor_id');
    const sessionStart = parseInt(sessionStorage.getItem('carcortex_session_start') || '0');
    const pageCount = parseInt(sessionStorage.getItem('carcortex_page_count') || '0');
    
    if (!sessionId) return;
    
    const duration = Math.floor((Date.now() - sessionStart) / 1000);
    
    // Use sendBeacon for reliable delivery on page unload
    const payload = JSON.stringify({
      event_type: 'session_end',
      session_id: sessionId,
      visitor_id: visitorId,
      page_url: window.location.pathname,
      exit_reason: exitReason,
      session_duration: duration,
      pages_viewed: pageCount
    });
    
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/api/analytics/track/session`, payload);
    } else {
      fetch(`${API}/api/analytics/track/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(() => {});
    }
    
  } catch (error) {
    console.debug('Session end tracking error:', error);
  }
};

// Track conversion events
export const trackConversion = (eventType, funnelStep = null, value = null) => {
  try {
    const sessionId = sessionStorage.getItem('carcortex_session_id');
    const visitorId = localStorage.getItem('carcortex_visitor_id');
    
    fetch(`${API}/api/analytics/track/conversion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        session_id: sessionId,
        visitor_id: visitorId,
        page_url: window.location.pathname,
        funnel_step: funnelStep,
        value: value
      }),
      keepalive: true
    }).catch(() => {});
    
  } catch (error) {
    console.debug('Conversion tracking error:', error);
  }
};

// Initialize tracking
export const initAnalytics = () => {
  // Track session start on first load
  const isNewSession = !sessionStorage.getItem('carcortex_session_id');
  if (isNewSession) {
    trackSessionStart();
  }
  
  // Track page view
  trackPageView();
  
  // Track session end on page unload
  window.addEventListener('beforeunload', () => {
    trackSessionEnd('close');
  });
  
  // Track session end on visibility change (tab close/switch)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackSessionEnd('navigation');
    }
  });
};

// Export default for easy import
export default {
  initAnalytics,
  trackPageView,
  trackSessionStart,
  trackSessionEnd,
  trackConversion
};
